import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { 
  XBRLFormSchema, 
  FormData, 
  FormColumn, 
  ValidationError, 
  FormState,
  ComponentConfig,
  ConceptTree,
  FormField,
  FieldType,
  SubmissionData,
  SubmissionEntry,
  FormSection
} from '../schema/types';
import { XBRLFormInput } from '../schema/xbrl-types';
import { FormValidator } from '../utils/validation';
import { XBRLFormBuilder } from '../utils/xbrl-form-builder';
import { AddColumnRequest, AvailableDimension } from './add-column-dialog';
import './form-section';
import './filter-roles-dialog';

@customElement('jupiter-dynamic-form')
export class JupiterDynamicForm extends LitElement {
  @property({ type: Object }) schema?: XBRLFormSchema;
  @property({ type: Object }) xbrlInput?: XBRLFormInput;
  @property({ type: Object }) config: ComponentConfig = {};
  @property({ type: Object }) initialData: FormData = {};
  @property({ type: Boolean }) disabled = false;
  @property({ type: Boolean }) readonly = false;
  @property({ type: String }) periodStartDate = '2025-01-01';
  @property({ type: String }) periodEndDate = '2025-12-31';

  @state() private _formData: FormData = {};
  @state() private _preservedFormData: FormData = {}; // Preserve data for hidden sections
  @state() private _typedMemberData: { [columnId: string]: { [axisId: string]: string } } = {};
  @state() private _preservedTypedMemberData: { [columnId: string]: { [axisId: string]: string } } = {}; // Preserve typed data for hidden sections
  @state() private _columns: FormColumn[] = [];
  @state() private _errors: ValidationError[] = [];
  @state() private _touched = new Set<string>();
  @state() private _dirty = false;
  @state() private _valid = true;
  @state() private _submitted = false;
  @state() private _currentSchema?: XBRLFormSchema;
  @state() private _allSections: FormSection[] = [];
  @state() private _selectedRoleIds: string[] = [];
  @state() private _showFilterDialog = false;

  static styles = css`
    :host {
      display: block;
      font-family: var(--jupiter-font-family, system-ui, -apple-system, sans-serif);
      color: var(--jupiter-text-primary, #333);
      background: var(--jupiter-background, #fff);
    }

    .form-container {
      width: 100%;
      max-width: var(--jupiter-max-width, none);
      margin: 0 auto;
    }

    .form-header {
      padding: 24px;
      border-bottom: 1px solid var(--jupiter-border-color, #ddd);
      background: var(--jupiter-form-header-background, #fff);
    }

    .form-title {
      font-size: 24px;
      font-weight: 600;
      margin: 0 0 8px 0;
      color: var(--jupiter-text-primary, #333);
    }

    .form-description {
      font-size: 16px;
      color: var(--jupiter-text-secondary, #666);
      margin: 0;
      line-height: 1.5;
    }

    .form-actions {
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      padding: 16px 24px;
      border-top: 1px solid var(--jupiter-border-color, #ddd);
      background: var(--jupiter-form-actions-background, #f8f9fa);
      display: flex;
      gap: 12px;
      align-items: center;
      z-index: 1000;
      box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
    }

    .form-sections {
      padding: 24px;
      padding-bottom: 100px; /* Add space for fixed footer */
    }

    .validation-summary {
      margin-bottom: 16px;
      padding: 16px;
      border-radius: 4px;
      background: var(--jupiter-error-background, #ffebee);
      border: 1px solid var(--jupiter-error-color, #d32f2f);
    }

    .validation-summary-title {
      font-weight: 600;
      color: var(--jupiter-error-color, #d32f2f);
      margin: 0 0 8px 0;
    }

    .validation-summary-list {
      margin: 0;
      padding-left: 20px;
    }

    .validation-summary-item {
      color: var(--jupiter-error-color, #d32f2f);
      margin-bottom: 4px;
    }

    .form-actions button {
      padding: 10px 20px;
      border: none;
      border-radius: 4px;
      font-size: 14px;
      font-weight: 500;
      cursor: pointer;
      transition: background-color 0.2s ease;
    }

    .btn-primary {
      background: var(--jupiter-primary-color, #1976d2);
      color: white;
    }

    .btn-primary:hover:not(:disabled) {
      background: var(--jupiter-primary-color-dark, #1565c0);
    }

    .btn-secondary {
      background: var(--jupiter-secondary-color, #757575);
      color: white;
    }

    .btn-secondary:hover:not(:disabled) {
      background: var(--jupiter-secondary-color-dark, #616161);
    }

    .btn-outline {
      background: transparent;
      color: var(--jupiter-primary-color, #1976d2);
      border: 1px solid var(--jupiter-primary-color, #1976d2);
    }

    .btn-outline:hover:not(:disabled) {
      background: var(--jupiter-primary-color, #1976d2);
      color: white;
    }

    button:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    .form-meta {
      display: flex;
      gap: 16px;
      align-items: center;
      font-size: 12px;
      color: var(--jupiter-text-secondary, #666);
      margin-left: auto;
    }

    .filter-roles-button {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px 16px;
      background: transparent;
      border: 1px solid var(--jupiter-primary-color, #1976d2);
      color: var(--jupiter-primary-color, #1976d2);
      border-radius: 4px;
      font-size: 13px;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.2s ease;
      margin-right: auto;
    }

    .filter-roles-button:hover:not(:disabled) {
      background: var(--jupiter-primary-color, #1976d2);
      color: white;
    }

    .filter-roles-button .filter-icon {
      width: 16px;
      height: 16px;
      fill: currentColor;
    }

    .roles-count {
      display: inline-flex;
      align-items: center;
      background: var(--jupiter-primary-color, #1976d2);
      color: white;
      border-radius: 10px;
      font-size: 11px;
      font-weight: 600;
      padding: 2px 6px;
      margin-left: 4px;
      min-width: 18px;
      justify-content: center;
    }

    .no-roles-message {
      padding: 40px;
      text-align: center;
      color: var(--jupiter-text-secondary, #666);
      background: var(--jupiter-background-light, #f8f9fa);
      border-radius: 8px;
      margin: 20px 0;
    }

    .no-roles-message h3 {
      margin: 0 0 16px 0;
      color: var(--jupiter-text-primary, #333);
    }

    .no-roles-message p {
      margin: 8px 0;
      line-height: 1.5;
    }
  `;

  connectedCallback() {
    super.connectedCallback();
    this._initializeForm();
  }

  updated(changedProperties: Map<string, any>) {
    if (changedProperties.has('xbrlInput') || changedProperties.has('schema')) {
      this._initializeForm();
    }
  }

  private _initializeForm() {
    // Generate schema from XBRL input if provided, otherwise use provided schema
    if (this.xbrlInput) {
      try {
        console.log('🔄 Initializing form from XBRL input:', this.xbrlInput);
        console.log('📅 Using period dates:', this.periodStartDate, 'to', this.periodEndDate);
        this._currentSchema = XBRLFormBuilder.buildFormSchema(
          this.xbrlInput, 
          this.periodStartDate, 
          this.periodEndDate
        );
        console.log('✅ Generated schema with sections:', this._currentSchema.sections.length);
        
        // Store all sections for filtering
        this._allSections = [...this._currentSchema.sections];
        
        // Initialize selected roles - always let user choose which roles to display
        if (this._selectedRoleIds.length === 0) {
          // Don't preselect any roles - let user choose
          this._selectedRoleIds = [];
          // Show dialog by default to let user choose
          this._showFilterDialog = true;
          // Immediately set empty sections for consistency
          this._currentSchema = {
            ...this._currentSchema,
            sections: []
          };
        }
        
        // Apply current role filter - this will show empty form if no roles selected
        this._applyRoleFilter();
        
        // Use simple column structure for now
        this._columns = [
          {
            id: 'base',
            title: 'Value',
            description: 'Base values for concepts',
            type: 'base',
            order: 0,
            removable: false
          }
        ];
      } catch (error) {
        console.error('❌ Error building form from XBRL input:', error);
        this._currentSchema = this._getDefaultSchema();
        this._allSections = [];
        this._selectedRoleIds = [];
        this._columns = this._getDefaultColumns();
      }
    } else if (this.schema) {
      this._currentSchema = this.schema;
      this._allSections = [...this.schema.sections];
      this._selectedRoleIds = this._allSections.map(section => section.id);
      this._columns = this._getDefaultColumns();
    } else {
      this._currentSchema = this._getDefaultSchema();
      this._allSections = [];
      this._selectedRoleIds = [];
      this._columns = this._getDefaultColumns();
    }

    // Initialize form data
    this._formData = { ...this.initialData };
    if (this.xbrlInput?.initialData) {
      this._formData = { ...this._formData, ...this.xbrlInput.initialData };
    }
    
    // Validate initial data
    this._validateForm();
  }

  private _getDefaultSchema(): XBRLFormSchema {
    return {
      version: '1.0',
      formId: 'default-form',
      title: 'Dynamic Form',
      description: 'Please provide form schema or XBRL input data',
      sections: []
    };
  }

  private _getDefaultColumns(): FormColumn[] {
    return [
      {
        id: 'base',
        title: 'Value',
        description: 'Base values for concepts',
        type: 'base',
        order: 0,
        removable: false
      }
    ];
  }

  private _applyRoleFilter() {
    if (!this._currentSchema || !this._allSections.length) return;
    
    console.log(`🔍 Applying role filter: ${this._selectedRoleIds.length} selected out of ${this._allSections.length} total`);
    
    // If no roles are selected, show empty form (user needs to select roles)
    if (this._selectedRoleIds.length === 0) {
      console.log(`📝 No roles selected - showing empty form`);
      this._currentSchema = {
        ...this._currentSchema,
        sections: []
      };
      return;
    }
    
    // Before filtering, preserve data from sections that will be hidden
    this._preserveDataForHiddenSections();
    
    // Filter sections based on selected role IDs
    const filteredSections = this._allSections.filter(section => 
      this._selectedRoleIds.includes(section.id)
    );
    
    // Restore data for sections that are now being shown
    this._restoreDataForVisibleSections(filteredSections);
    
    // Update current schema with filtered sections
    this._currentSchema = {
      ...this._currentSchema,
      sections: filteredSections
    };
  }

  private _preserveDataForHiddenSections() {
    // Get currently visible section IDs
    const currentlyVisibleSectionIds = this._currentSchema?.sections.map(s => s.id) || [];
    
    // Find sections that will be hidden after filter is applied
    const sectionsThatWillBeHidden = currentlyVisibleSectionIds.filter(
      sectionId => !this._selectedRoleIds.includes(sectionId)
    );
    
    // Preserve form data for concepts in hidden sections
    sectionsThatWillBeHidden.forEach(sectionId => {
      const section = this._allSections.find(s => s.id === sectionId);
      if (section) {
        this._preserveSectionData(section);
      }
    });
    
    console.log(`📦 Preserved data for ${sectionsThatWillBeHidden.length} hidden sections:`, sectionsThatWillBeHidden);
  }

  private _restoreDataForVisibleSections(visibleSections: FormSection[]) {
    // Restore data for sections that are now being shown
    visibleSections.forEach(section => {
      this._restoreSectionData(section);
    });
    
    console.log(`🔄 Restored data for ${visibleSections.length} visible sections`);
  }

  private _preserveSectionData(section: FormSection) {
    // Preserve form data for all concepts in this section
    section.concepts.forEach(concept => {
      this._preserveConceptData(concept);
    });
  }

  private _preserveConceptData(concept: ConceptTree) {
    // Preserve data for this concept
    if (this._formData[concept.id]) {
      this._preservedFormData[concept.id] = { ...this._formData[concept.id] };
      console.log(`💾 Preserved form data for concept: ${concept.id}`);
    }
    
    // Preserve typed member data for columns related to this concept
    concept.fields.forEach(field => {
      if (this._typedMemberData[field.columnId]) {
        this._preservedTypedMemberData[field.columnId] = { ...this._typedMemberData[field.columnId] };
        console.log(`💾 Preserved typed data for column: ${field.columnId}`);
      }
    });
    
    // Recursively preserve data for child concepts
    if (concept.children) {
      concept.children.forEach(child => {
        this._preserveConceptData(child);
      });
    }
  }

  private _restoreSectionData(section: FormSection) {
    // Restore form data for all concepts in this section
    section.concepts.forEach(concept => {
      this._restoreConceptData(concept);
    });
  }

  private _restoreConceptData(concept: ConceptTree) {
    // Restore data for this concept if it was preserved
    if (this._preservedFormData[concept.id]) {
      this._formData[concept.id] = { ...this._preservedFormData[concept.id] };
      console.log(`🔄 Restored form data for concept: ${concept.id}`);
    }
    
    // Restore typed member data for columns related to this concept
    concept.fields.forEach(field => {
      if (this._preservedTypedMemberData[field.columnId]) {
        this._typedMemberData[field.columnId] = { ...this._preservedTypedMemberData[field.columnId] };
        console.log(`🔄 Restored typed data for column: ${field.columnId}`);
      }
    });
    
    // Recursively restore data for child concepts
    if (concept.children) {
      concept.children.forEach(child => {
        this._restoreConceptData(child);
      });
    }
  }

  private _shouldShowFilterButton(): boolean {
    return true; // Always show filter button to allow role selection
  }

  private _handleFilterRolesClick() {
    this._showFilterDialog = true;
  }

  private _handleFilterDialogCancel() {
    this._showFilterDialog = false;
    
    // If no roles were previously selected (first time opening), 
    // and user cancels, keep empty selection for consistency
    console.log(`🚫 Filter dialog cancelled. Current selection: ${this._selectedRoleIds.length}/${this._allSections.length}`);
  }

  private _handleRoleFilterApply(event: CustomEvent) {
    const { selectedRoleIds } = event.detail;
    this._selectedRoleIds = selectedRoleIds;
    this._applyRoleFilter();
    this._showFilterDialog = false;
    
    console.log(`🎯 Applied role filter: ${selectedRoleIds.length}/${this._allSections.length} roles selected`);
    
    // Dispatch event for external listeners
    this.dispatchEvent(new CustomEvent('roles-filter-changed', {
      detail: { 
        selectedRoleIds, 
        totalRoles: this._allSections.length,
        visibleRoles: selectedRoleIds.length
      },
      bubbles: true
    }));
  }

  private _validateForm() {
    const errors: ValidationError[] = [];
    
    // Use current schema for validation
    const schema = this._currentSchema;
    if (!schema) return;
    
    // Validate each section and its concepts
    for (const section of schema.sections) {
      for (const concept of section.concepts) {
        for (const field of concept.fields) {
          const value = this._formData[concept.id]?.[field.columnId];
          const fieldErrors = FormValidator.validateField(
            field.id,
            concept.id,
            field.columnId,
            value,
            field.type,
            field.validation || []
          );
          errors.push(...fieldErrors);
        }
      }
    }

    this._errors = errors;
    this._valid = errors.filter(e => e.severity === 'error').length === 0;
  }

  private _handleFieldChange(event: CustomEvent) {
    const { fieldId, conceptId, columnId, value } = event.detail;
    
    // Update form data with proper immutable update to trigger Lit reactivity
    const updatedFormData = { ...this._formData };
    if (!updatedFormData[conceptId]) {
      updatedFormData[conceptId] = {};
    }
    const oldValue = updatedFormData[conceptId][columnId];
    updatedFormData[conceptId] = { ...updatedFormData[conceptId], [columnId]: value };
    this._formData = updatedFormData;
    
    // Mark as touched and dirty
    this._touched.add(`${conceptId}-${columnId}`);
    this._dirty = true;
    
    // Re-validate
    this._validateForm();
    
    // Trigger update (should be automatic with the new object reference)
    this.requestUpdate();
    
    // Dispatch change event
    this.dispatchEvent(new CustomEvent('field-change', {
      detail: { fieldId, conceptId, columnId, value, oldValue },
      bubbles: true
    }));
  }

  /**
   * Generate a unique concept key that includes section context
   */
  private _generateUniqueConceptKey(sectionId: string, conceptId: string): string {
    return `${sectionId}::${conceptId}`;
  }

  /**
   * Extract the original concept ID from a unique concept key
   */
  private _extractOriginalConceptId(uniqueConceptKey: string): string {
    const parts = uniqueConceptKey.split('::');
    return parts.length > 1 ? parts[1] : uniqueConceptKey;
  }

  private _handleTypedMemberChange(event: CustomEvent) {
    const { columnId, axisId, value } = event.detail;
    
    console.log(`🔍 [DynamicForm] Typed member change: columnId=${columnId}, axisId=${axisId}, value=${value}`);
    
    // Update typed member data with immutable update
    const updatedTypedMemberData = { ...this._typedMemberData };
    if (!updatedTypedMemberData[columnId]) {
      updatedTypedMemberData[columnId] = {};
    }
    updatedTypedMemberData[columnId] = { ...updatedTypedMemberData[columnId], [axisId]: value };
    this._typedMemberData = updatedTypedMemberData;
    
    // Mark as dirty
    this._dirty = true;
    
    // Trigger update
    this.requestUpdate();
    
    // Dispatch change event
    this.dispatchEvent(new CustomEvent('typed-member-change', {
      detail: { columnId, axisId, value },
      bubbles: true
    }));
  }

  private _handleSectionExpand(event: CustomEvent) {
    this.dispatchEvent(new CustomEvent('section-expand', {
      detail: event.detail,
      bubbles: true
    }));
  }

  private _handleConceptExpand(event: CustomEvent) {
    this.dispatchEvent(new CustomEvent('concept-expand', {
      detail: event.detail,
      bubbles: true
    }));
  }

  private _handleColumnRemove(event: CustomEvent) {
    const { columnId, sectionId } = event.detail;
    
    // Remove column from ONLY the specific section
    if (this._currentSchema && sectionId) {
      const targetSection = this._currentSchema.sections.find(section => section.id === sectionId);
      if (targetSection && targetSection.columns) {
        targetSection.columns = targetSection.columns.filter(col => col.id !== columnId);
      }
    } else {
      // Fallback: remove from all sections (for backward compatibility)
      this._columns = this._columns.filter(col => col.id !== columnId);
      
      if (this._currentSchema) {
        this._currentSchema.sections.forEach(section => {
          if (section.columns) {
            section.columns = section.columns.filter(col => col.id !== columnId);
          }
        });
      }
    }
    
    // Remove data for this column from the specific section's concepts
    this._removeColumnData(columnId, sectionId);
    
    this._dirty = true;
    this._validateForm();
    this.requestUpdate();
    
    this.dispatchEvent(new CustomEvent('column-remove', {
      detail: { columnId, sectionId },
      bubbles: true
    }));
  }

  private _handleColumnAddRequest(event: CustomEvent) {
    const { sectionId, columnRequest } = event.detail;
    this._addColumnFromRequest(columnRequest, sectionId);
  }

  private _getAvailableDimensionsForSection(sectionId: string): AvailableDimension[] {
    console.log(`🔍 Getting dimensions for section: ${sectionId}`);
    
    if (!this.xbrlInput?.hypercubes?.[0]) {
      console.log('❌ No hypercubes data available');
      return [];
    }

    //console.log(`📊 Available hypercube roles:`, this.xbrlInput.hypercubes[0].roles.map(r => r.roleId));
    
    const hypercubeRole = this.xbrlInput.hypercubes[0].roles.find(hr => hr.roleId === sectionId);
    
    if (!hypercubeRole?.items?.length) {
      console.log(`❌ No hypercube items found for role: ${sectionId}`);
      return [];
    }

    console.log(`✅ Found hypercube role with ${hypercubeRole.items.length} items`);
    
    const availableDimensions: AvailableDimension[] = [];

    // Process all hypercube items to find unique dimensions
    hypercubeRole.items.forEach(item => {
      if (item.dimensions && item.dimensions.length > 0) {
        item.dimensions.forEach(dimension => {
          // Check if we already have this dimension
          if (!availableDimensions.find(d => d.id === dimension.id)) {
            const axisLabel = dimension.labels.find(l => l.role === 'http://www.xbrl.org/2003/role/label')?.label || dimension.conceptName;
            
            const availableDimension: AvailableDimension = {
              id: dimension.id,
              conceptName: dimension.conceptName,
              axisLabel: axisLabel
            };

            // Add members if available
            if (dimension.members && dimension.members.length > 0) {
              availableDimension.members = dimension.members.map(member => ({
                id: member.id,
                label: member.labels?.find(l => l.role === 'http://www.xbrl.org/2003/role/label')?.label || member.conceptName || member.id
              }));
            }

            // Add typed member if available
            if (dimension.typedMember) {
              availableDimension.typedMember = {
                id: dimension.typedMember.id,
                dataType: 'string' // Default to string for now
              };
            }

            availableDimensions.push(availableDimension);
          }
        });
      }
    });

    console.log(`📈 Found ${availableDimensions.length} dimensions for section ${sectionId}:`, availableDimensions.map(d => d.axisLabel));
    return availableDimensions;
  }

  private _addColumnFromRequest(request: AddColumnRequest, sectionId: string) {
    const timestamp = Date.now();
    const newColumnId = `col-${timestamp}`;
    
    let title = '';
    let description = '';
    
    if (request.periodType === 'instant') {
      title = 'Current';
      description = `Values as of ${request.instantDate}`;
    } else if (request.periodType === 'duration' || request.periodType === 'mixed') {
      title = 'Current Period';
      description = `Period: ${request.startDate} to ${request.endDate}`;
    }
    
    // Process selected dimensions from the dialog
    let dimensionData: any = {
      dimensionId: 'period',
      memberValue: request.periodType === 'instant' ? 
        `instant-${request.instantDate}` : 
        `duration-${request.startDate}-${request.endDate}`,
      memberLabel: title,
    };
    
    // If dimensions were selected in the dialog, use them
    if (request.selectedDimensions && request.selectedDimensions.length > 0) {
      const dimensionParts: string[] = [];
      const dimensionKeys: string[] = [];
      
      for (const dimSelection of request.selectedDimensions) {
        if (dimSelection.isTyped && dimSelection.typedValue) {
          // Typed dimension
          dimensionParts.push(`${dimSelection.axisLabel}: ${dimSelection.typedValue}`);
          dimensionKeys.push(`${dimSelection.axisId}|${dimSelection.typedValue}`);
        } else if (dimSelection.memberId && dimSelection.memberLabel) {
          // Domain dimension with selected member
          dimensionParts.push(dimSelection.memberLabel);
          dimensionKeys.push(`${dimSelection.axisId}|${dimSelection.memberId}`);
        }
      }
      
      // Build combined title with dimensions
      const periodPart = request.periodType === 'instant' ? 
        `(${request.instantDate})` : 
        `(${request.startDate} / ${request.endDate})`;
      
      if (dimensionParts.length > 0) {
        title = `${dimensionParts.join(' | ')} ${periodPart}`;
        
        dimensionData = {
          dimensionId: newColumnId,
          memberValue: dimensionParts.join(' | '),
          memberLabel: dimensionParts.join(' | '),
          memberKey: dimensionParts.join(' | '),
          dimensionIdKey: dimensionKeys.join('::'),
          selectedDimensions: request.selectedDimensions
        };
        
        console.log(`📊 Applied selected dimensions to new column: ${dimensionData.memberKey}`);
      }
    } else {
      // Fallback to existing hypercube logic for backwards compatibility
      if (this.xbrlInput?.hypercubes?.[0]) {
        const hypercubeRole = this.xbrlInput.hypercubes[0].roles.find(hr => hr.roleId === sectionId);
        
        if (hypercubeRole?.items?.length === 1) {
          const item = hypercubeRole.items[0];
          if (item.dimensions.length === 1) {
            const dimension = item.dimensions[0];
            if (dimension.members && dimension.members.length > 0) {
              // Find axis label
              const axisLabel = dimension.labels.find(l => l.role === 'http://www.xbrl.org/2003/role/label')?.label || dimension.conceptName;
              
              // For manually added columns, we could offer choice or use first member
              // For now, using first member as specified in requirements
              const firstMember = dimension.members[0];
              const memberLabel = firstMember.labels.find(l => l.role === 'http://www.xbrl.org/2003/role/label')?.label || firstMember.conceptName;
              
              // Update title to include dimension information
              const periodPart = request.periodType === 'instant' ? 
                `(${request.instantDate})` : 
                `(${request.startDate} / ${request.endDate})`;
              title = `${memberLabel} ${periodPart}`;
              
              dimensionData = {
                dimensionId: newColumnId,
                axisId: dimension.id,
                memberId: firstMember.id,
                memberValue: memberLabel,
                memberLabel: memberLabel,
                axis: axisLabel,
                axisLabel: axisLabel,
                memberKey: `${axisLabel} | ${memberLabel}`,
                dimensionIdKey: `${dimension.id} | ${firstMember.id}`
              };
              
              console.log(`📊 Applied hypercube dimension to new column: ${dimensionData.memberKey}`);
            }
          }
        }
      }
    }
    
    const newColumn: FormColumn = {
      id: newColumnId,
      title,
      description,
      type: dimensionData.memberKey ? 'dimension' : 'dimension',
      order: this._columns.length,
      removable: true,
      dimensionData
    };

    // Store typed member data for the new column if typed dimensions were selected
    if (request.selectedDimensions && request.selectedDimensions.length > 0) {
      const typedMembers: { [axisId: string]: string } = {};
      let hasTypedDimensions = false;
      
      for (const dimSelection of request.selectedDimensions) {
        if (dimSelection.isTyped) {
          // Initialize typed member data with provided value or empty string
          typedMembers[dimSelection.axisId] = dimSelection.typedValue || '';
          hasTypedDimensions = true;
          console.log(`💾 Initializing typed member data: ${dimSelection.axisLabel} = "${dimSelection.typedValue || '(empty - will be entered in column header)'}"`);
        }
      }
      
      if (hasTypedDimensions) {
        this._typedMemberData[newColumnId] = typedMembers;
        // Update dimension data to indicate it has typed members and include the typed member info
        if (newColumn.dimensionData) {
          newColumn.dimensionData.hasTypedMembers = true;
          // Create typedMembers array for the form-section template
          newColumn.dimensionData.typedMembers = request.selectedDimensions
            .filter(dim => dim.isTyped)
            .map(dim => ({
              axisId: dim.axisId,
              axisLabel: dim.axisLabel,
              typedMemberId: dim.axisId, // Use axisId as typedMemberId for typed dimensions
              memberLabel: dim.axisLabel // Use axisLabel as memberLabel
            }));
          console.log(`� Added typed members to column dimension data:`, newColumn.dimensionData.typedMembers);
        }
        console.log(`� Initialized typed member data for column ${newColumnId}:`, typedMembers);
      }
    }
    
    // Add column ONLY to the specific section that requested it
    if (this._currentSchema) {
      const targetSection = this._currentSchema.sections.find(section => section.id === sectionId);
      if (targetSection) {
        if (!targetSection.columns) {
          targetSection.columns = [...this._columns];
        }
        targetSection.columns = [...targetSection.columns, newColumn];
      }
    }
    
    // Replicate form fields for existing concepts in ONLY the target section
    this._replicateFieldsForNewColumn(newColumnId, request, sectionId);
    
    this._dirty = true;
    this.requestUpdate();
    
    this.dispatchEvent(new CustomEvent('column-add', {
      detail: { column: newColumn },
      bubbles: true
    }));
  }

  private _removeColumnData(columnId: string, sectionId?: string) {
    if (!this._currentSchema) return;
    
    if (sectionId) {
      // Remove data only for concepts in the specific section
      const targetSection = this._currentSchema.sections.find(section => section.id === sectionId);
      if (targetSection) {
        this._removeColumnDataFromConcepts(targetSection.concepts, columnId);
      }
    } else {
      // Remove data for all concepts in all sections
      for (const conceptId in this._formData) {
        if (this._formData[conceptId][columnId] !== undefined) {
          delete this._formData[conceptId][columnId];
        }
      }
    }
  }

  private _removeColumnDataFromConcepts(concepts: ConceptTree[], columnId: string) {
    concepts.forEach(concept => {
      // Remove form data for this concept and column
      if (this._formData[concept.id] && this._formData[concept.id][columnId] !== undefined) {
        delete this._formData[concept.id][columnId];
      }
      
      // Remove field from concept's fields array
      if (concept.fields) {
        concept.fields = concept.fields.filter(field => field.columnId !== columnId);
      }
      
      // Recursively process children
      if (concept.children) {
        this._removeColumnDataFromConcepts(concept.children, columnId);
      }
    });
  }

  private _shouldCreateFieldForConcept(concept: ConceptTree, request: AddColumnRequest): boolean {
    // Don't create fields for abstract concepts
    if (concept.abstract) {
      return false;
    }
    
    // Check period type compatibility
    if (concept.periodType) {
      if (request.periodType === 'instant' && concept.periodType !== 'instant') {
        return false;
      }
      if (request.periodType === 'duration' && concept.periodType !== 'duration') {
        return false;
      }
    }
    
    return true;
  }

  private _replicateFieldsForNewColumn(columnId: string, request: AddColumnRequest, sectionId: string) {
    if (!this._currentSchema) return;
    
    // Build fields for the new column for ONLY the specific section
    const formBuilder = new XBRLFormBuilder();
    
    const targetSection = this._currentSchema.sections.find(section => section.id === sectionId);
    if (targetSection) {
      this._replicateFieldsForSection(targetSection.concepts, columnId, request, formBuilder);
    }
  }

  private _replicateFieldsForSection(concepts: ConceptTree[], columnId: string, request: AddColumnRequest, formBuilder: XBRLFormBuilder) {
    concepts.forEach(concept => {
      // Check if we should create a field for this concept in the new column
      if (this._shouldCreateFieldForConcept(concept, request)) {
        // Create a new field manually based on existing field structure or from scratch
        const existingField = concept.fields?.[0]; // Use first existing field as template
        
        let field: FormField;
        if (existingField) {
          // Template from existing field
          field = {
            id: `${concept.id}_${columnId}`,
            conceptId: concept.id,
            columnId: columnId,
            type: existingField.type,
            label: existingField.label,
            placeholder: existingField.placeholder,
            required: existingField.required,
            disabled: existingField.disabled,
            validation: existingField.validation,
            defaultValue: existingField.defaultValue,
            // Set period dates from the add column request
            periodStartDate: request.periodType === 'instant' ? request.instantDate : request.startDate,
            periodEndDate: request.periodType === 'instant' ? request.instantDate : request.endDate
          };
        } else {
          // Create field from scratch for concepts without existing fields
          field = {
            id: `${concept.id}_${columnId}`,
            conceptId: concept.id,
            columnId: columnId,
            type: 'text' as FieldType,
            label: concept.label || concept.name,
            placeholder: `Enter ${concept.name}`,
            required: false,
            disabled: false,
            validation: [],
            defaultValue: '',
            // Set period dates from the add column request
            periodStartDate: request.periodType === 'instant' ? request.instantDate : request.startDate,
            periodEndDate: request.periodType === 'instant' ? request.instantDate : request.endDate
          };
        }
        
        // Add field to concept's fields array
        if (!concept.fields) {
          concept.fields = [];
        }
        concept.fields.push(field);
      }
      
      // Recursively process children
      if (concept.children) {
        this._replicateFieldsForSection(concept.children, columnId, request, formBuilder);
      }
    });
  }

  private _addColumn() {
    // This would typically open a dialog to configure the new dimension column
    // For now, we'll add a generic dimension column
    const newColumnId = `dim-${Date.now()}`;
    const newColumn: FormColumn = {
      id: newColumnId,
      title: `Dimension ${this._columns.length}`,
      description: 'Additional dimension column',
      type: 'dimension',
      order: this._columns.length,
      removable: true,
      dimensionData: {
        dimensionId: 'custom',
        memberValue: 'default',
        memberLabel: 'Default Member',
      }
    };
    
    this._columns = [...this._columns, newColumn];
    this.requestUpdate();
    
    this.dispatchEvent(new CustomEvent('column-add', {
      detail: { column: newColumn },
      bubbles: true
    }));
  }

  private _handleSubmit() {
    console.log('📝 Form submission started...');
    this._submitted = true;
    this._validateForm();
    
    // Generate submission data in the required format
    const submissionData = this._generateSubmissionData();
    
    // Log final submission data for debugging
    console.log('📊 Form Submission Data:', JSON.stringify(submissionData, null, 2));
    
    this.dispatchEvent(new CustomEvent('form-submit', {
      detail: {
        data: this._formData,
        submissionData: submissionData,
        valid: this._valid,
        errors: this._errors
      },
      bubbles: true
    }));
    
    console.log('✅ Form submit event dispatched');
  }

  private _handleSaveDraft() {
    // Generate draft data in the same format as submission
    const draftData = this._generateSubmissionData();
    
    this.dispatchEvent(new CustomEvent('form-save-draft', {
      detail: {
        data: this._formData,
        draftData: draftData,
        valid: this._valid,
        errors: this._errors
      },
      bubbles: true
    }));
  }

  private _generateSubmissionData(): SubmissionData {
    const submissionData: SubmissionData = [];
    
    if (!this._currentSchema) {
      return submissionData;
    }

    // Include data from currently visible sections
    this._currentSchema.sections.forEach(section => {
      this._processConceptsForSubmission(section.concepts, submissionData, section);
    });

    // IMPORTANT: Also include data from preserved (hidden) sections
    this._includePreservedDataInSubmission(submissionData);

    // Remove duplicates from submission data
    const uniqueSubmissionData = this._removeDuplicateSubmissions(submissionData);
    
    return uniqueSubmissionData;
  }

  private _includePreservedDataInSubmission(submissionData: SubmissionData) {
    // Process preserved form data
    Object.keys(this._preservedFormData).forEach(conceptId => {
      const conceptData = this._preservedFormData[conceptId];
      if (conceptData) {
        Object.keys(conceptData).forEach(columnId => {
          const value = conceptData[columnId];
          if (value !== undefined && value !== null && value !== '') {
            // Find the original concept from all sections to get proper submission data
            const originalConcept = this._findConceptInAllSections(conceptId);
            if (originalConcept) {
              const originalSection = this._findSectionForConcept(conceptId);
              this._addConceptDataToSubmission(originalConcept, columnId, value, submissionData, originalSection || undefined);
            }
          }
        });
      }
    });

    console.log(`📤 Included preserved data in submission. Total entries: ${submissionData.length}`);
  }

  private _findConceptInAllSections(conceptId: string): ConceptTree | null {
    for (const section of this._allSections) {
      const concept = this._findConceptInSection(section.concepts, conceptId);
      if (concept) {
        return concept;
      }
    }
    return null;
  }

  private _findConceptInSection(concepts: ConceptTree[], conceptId: string): ConceptTree | null {
    for (const concept of concepts) {
      if (concept.id === conceptId) {
        return concept;
      }
      if (concept.children) {
        const found = this._findConceptInSection(concept.children, conceptId);
        if (found) {
          return found;
        }
      }
    }
    return null;
  }

  private _findSectionForConcept(conceptId: string): FormSection | null {
    for (const section of this._allSections) {
      const concept = this._findConceptInSection(section.concepts, conceptId);
      if (concept) {
        return section;
      }
    }
    return null;
  }

  private _addConceptDataToSubmission(concept: ConceptTree, columnId: string, value: any, submissionData: SubmissionData, section?: FormSection) {
    // Find the matching field for this column
    const field = concept.fields.find(f => f.columnId === columnId);
    if (!field) return;

    // Determine period type for submission
    const isInstant = concept.periodType === 'instant';
    
    const entry: SubmissionEntry = {
      conceptId: concept.originalConceptId || concept.id,
      value: value,
      period: {
        type: concept.periodType || 'duration',
        ...(isInstant 
          ? { date: field.periodStartDate || this.periodStartDate }
          : { 
              startDate: field.periodStartDate || this.periodStartDate,
              endDate: field.periodEndDate || this.periodEndDate
            })
      }
    };

    // Add dimension information if available
    const column = this._findColumnByIdInAllSections(columnId);
    if (column?.dimensionData?.memberLabel) {
      entry.dimension = column.dimensionData.memberLabel;
    }

    submissionData.push(entry);
  }

  private _findColumnByIdInAllSections(columnId: string): FormColumn | undefined {
    for (const section of this._allSections) {
      if (section.columns) {
        const column = section.columns.find(col => col.id === columnId);
        if (column) {
          return column;
        }
      }
    }
    return undefined;
  }

  private _removeDuplicateSubmissions(submissionData: SubmissionData): SubmissionData {
    const uniqueEntries = new Map<string, any>();
    
    submissionData.forEach(entry => {
      // Create a unique key based on concept ID, value, and dimension
      const key = `${entry.conceptId}_${entry.value}_${entry.dimension || 'no-dimension'}`;
      
      // Only keep the first occurrence of each unique entry
      if (!uniqueEntries.has(key)) {
        uniqueEntries.set(key, entry);
      }
    });
    
    return Array.from(uniqueEntries.values());
  }

  private _findColumnById(columnId: string): FormColumn | undefined {
    if (!this._currentSchema) {
      return undefined;
    }

    for (const section of this._currentSchema.sections) {
      if (section.columns) {
        const column = section.columns.find(col => col.id === columnId);
        if (column) {
          return column;
        }
      }
    }
    return undefined;
  }

  private _findColumnByIdInSection(columnId: string, section?: FormSection): FormColumn | undefined {
    if (!section?.columns) {
      return undefined;
    }

    return section.columns.find(col => col.id === columnId);
  }

  private _getTypedMemberNameForAxis(axisId: string, section?: FormSection): string | null {
    // Look for the typed member name in the hypercube data
    if (!this.xbrlInput?.hypercubes?.[0]) {
      return null;
    }

    const sectionRoleId = section?.id;
    if (!sectionRoleId) {
      return null;
    }

    // Find the role in hypercubes
    const hypercube = this.xbrlInput.hypercubes[0];
    const role = hypercube.roles?.find((r: any) => r.roleId === sectionRoleId);
    if (!role?.items) {
      return null;
    }

    // Search through all items for dimensions matching the axis
    for (const item of role.items) {
      if (item.dimensions) {
        const dimension = item.dimensions.find((d: any) => d.id === axisId);
        if (dimension?.typedMember?.id) {
          return dimension.typedMember.id;
        }
      }
    }

    return null;
  }

  private _doesConceptApplyToTypedDimension(concept: ConceptTree, section?: FormSection, columnId?: string): boolean {
    // Check if this concept is part of a hypercube that has typed dimensions
    if (!this.xbrlInput?.hypercubes?.[0]) {
      return false;
    }

    const sectionRoleId = section?.id;
    if (!sectionRoleId) {
      return false;
    }

    // Find the hypercube role that matches this section
    const hypercubeRole = this.xbrlInput.hypercubes[0].roles.find(hr => hr.roleId === sectionRoleId);
    if (!hypercubeRole?.items?.length) {
      return false;
    }

    // Check if any hypercube item contains this concept and has typed dimensions
    for (const item of hypercubeRole.items) {
      // Check if this concept is in the conceptIds list for this hypercube item
      const conceptIdToCheck = concept.originalConceptId || concept.id;
      if (item.conceptIds && item.conceptIds.includes(conceptIdToCheck)) {
        // Check if this hypercube item has typed dimensions
        const hasTypedDimensions = item.dimensions.some(dim => dim.typedMember);
        console.log(`🔍 [DynamicForm] Concept ${conceptIdToCheck} found in hypercube item. Has typed dimensions:`, hasTypedDimensions);
        return hasTypedDimensions;
      }
    }

    console.log(`🔍 [DynamicForm] Concept ${concept.originalConceptId || concept.id} not found in any hypercube items for role ${sectionRoleId}`);
    return false;
  }

  /**
   * Get the specific typed dimensions that apply to a concept in its role
   */
  private _getApplicableTypedDimensions(concept: ConceptTree, section?: FormSection): string[] {
    if (!this.xbrlInput?.hypercubes?.[0] || !section?.id) {
      return [];
    }

    const hypercubeRole = this.xbrlInput.hypercubes[0].roles.find(hr => hr.roleId === section.id);
    if (!hypercubeRole?.items?.length) {
      return [];
    }

    const conceptIdToCheck = concept.originalConceptId || concept.id;
    
    // Find the hypercube item that contains this concept
    for (const item of hypercubeRole.items) {
      if (item.conceptIds && item.conceptIds.includes(conceptIdToCheck)) {
        // Return the IDs of typed dimensions in this hypercube item
        const typedDimensions = item.dimensions
          .filter(dim => dim.typedMember)
          .map(dim => dim.id);
        
        console.log(`🔍 [DynamicForm] Found applicable typed dimensions for concept ${conceptIdToCheck}:`, typedDimensions);
        return typedDimensions;
      }
    }

    return [];
  }

  private _processConceptsForSubmission(concepts: ConceptTree[], submissionData: SubmissionData, section?: FormSection) {
    const targetRole = "Toelichting op de geconsolideerde jaarrekening - Financiële vaste activa: Deelnemingen: Volledig geconsolideerd: Specificatie";
    const sectionTitle = section?.title || 'Unknown';
    
    concepts.forEach(concept => {
      // Enhanced debug logging for target role
      if (sectionTitle === targetRole) {
        console.warn(`[DUPLICATE DEBUG] Processing concept ${concept.originalConceptId || concept.id} in target role`);
        if (concept.fields) {
          console.warn(`[DUPLICATE DEBUG] Concept has ${concept.fields.length} fields`);
          concept.fields.forEach((field, index) => {
            console.warn(`[DUPLICATE DEBUG] Field ${index}: columnId=${field.columnId}, current value=${this._formData[concept.id]?.[field.columnId]}`);
          });
        }
      }
      
      // Debug log for any concept with multiple fields
      if (concept.fields && concept.fields.length > 1) {
        console.log(`🔍 [DEBUG] Processing concept ${concept.originalConceptId}, fields count: ${concept.fields.length}`);
        concept.fields.forEach((field, index) => {
          console.log(`  Field ${index}: columnId=${field.columnId}, value=${this._formData[concept.id]?.[field.columnId]}`);
        });
      }
      
      // Process fields for this concept
      if (concept.fields && concept.fields.length > 0) {
        concept.fields.forEach(field => {
          // Get the value from the nested form data structure
          const conceptData = this._formData[concept.id];
          const fieldValue = conceptData?.[field.columnId];
          
          // Only include fields that have values
          if (fieldValue !== undefined && fieldValue !== null && fieldValue !== '') {
            // Log submission entry creation for target role
            if (sectionTitle === targetRole) {
              console.warn(`[DUPLICATE DEBUG] Creating submission entry for concept ${concept.originalConceptId || concept.id}, field columnId=${field.columnId}, value=${fieldValue}`);
            }
            
            // Find the column information from the SPECIFIC section to check for dimension data
            const column = this._findColumnByIdInSection(field.columnId, section);
            console.log(`🔍 [DynamicForm] Processing field for concept ${concept.originalConceptId || concept.id}, field columnId: ${field.columnId}, found column in section ${section?.id}: ${!!column}, column title: ${column?.title}`);
            
            const submissionEntry: any = {
              conceptId: concept.id,
              value: fieldValue,
              period: {
                type: concept.periodType || 'duration'
              } as { type: 'instant' | 'duration'; date?: string; startDate?: string; endDate?: string; }
            };

            // Determine period structure based on concept's period type
            if (concept.periodType === 'instant') {
              // For instant concepts, use a single date
              submissionEntry.period.date = field.periodStartDate || this.periodStartDate;
            } else {
              // For duration concepts, use start and end dates
              submissionEntry.period.startDate = field.periodStartDate || this.periodStartDate;
              submissionEntry.period.endDate = field.periodEndDate || this.periodEndDate;
            }

            // Add dimension information
            // Only use dimension data from the specific column that this field is linked to
            if (column?.type === 'dimension' && column.dimensionData?.dimensionIdKey) {
              submissionEntry.dimension = column.dimensionData.dimensionIdKey;
              console.log(`🔍 [DynamicForm] Using dimension key from field's column (${field.columnId}):`, column.dimensionData.dimensionIdKey);
            } else {
              console.log(`🔍 [DynamicForm] No dimension data found for field column ${field.columnId}. Column type: ${column?.type}, has dimensionData: ${!!column?.dimensionData}`);
            }

            // Add typed member values if they exist for this column AND the concept is part of the hypercube
            if (this._typedMemberData[field.columnId]) {
              const applicableTypedDimensions = this._getApplicableTypedDimensions(concept, section);
              console.log(`🔍 [DynamicForm] Concept ${concept.id} applicable typed dimensions:`, applicableTypedDimensions);
              
              if (applicableTypedDimensions.length > 0) {
                // Filter typed member data to only include dimensions applicable to this concept
                const filteredTypedMembers: { [axisId: string]: { value: string; memberName: string } } = {};
                const allTypedMemberData = this._typedMemberData[field.columnId];
                
                applicableTypedDimensions.forEach(dimensionId => {
                  if (allTypedMemberData[dimensionId]) {
                    const memberName = this._getTypedMemberNameForAxis(dimensionId, section);
                    filteredTypedMembers[dimensionId] = {
                      value: allTypedMemberData[dimensionId],
                      memberName: memberName || 'Unknown'
                    };
                  }
                });
                
                if (Object.keys(filteredTypedMembers).length > 0) {
                  submissionEntry.typedMembers = filteredTypedMembers;
                  console.log(`🔍 [DynamicForm] Adding filtered typed members to submission:`, filteredTypedMembers);
                } else {
                  console.log(`🔍 [DynamicForm] No typed member values found for applicable dimensions:`, applicableTypedDimensions);
                }
              } else {
                console.log(`🔍 [DynamicForm] Skipping typed members for concept ${concept.id} - not applicable to this hypercube`);
              }
            } else {
              console.log(`🔍 [DynamicForm] No typed member data found for column ${field.columnId}. Available columns:`, Object.keys(this._typedMemberData));
              if (column) {
                console.log(`🔍 [DynamicForm] Column details:`, {
                  id: column.id,
                  type: column.type,
                  hasTypedMembers: column.dimensionData?.hasTypedMembers,
                  dimensionData: column.dimensionData
                });
              }
            }

            // Log final submission entry for target role
            if (sectionTitle === targetRole) {
              console.warn(`[DUPLICATE DEBUG] Final submission entry:`, submissionEntry);
            }

            submissionData.push(submissionEntry);
          }
        });
      }

      // Recursively process children
      if (concept.children) {
        this._processConceptsForSubmission(concept.children, submissionData, section);
      }
    });
  }

  private _handleReset() {
    this._formData = { ...this.initialData };
    this._touched.clear();
    this._dirty = false;
    this._submitted = false;

    // Clear preserved data when form is reset
    this._preservedFormData = {};
    this._preservedTypedMemberData = {};

    console.log('🔄 Form reset - all data and preserved data cleared');

    this._validateForm();
    this.requestUpdate();
    
    this.dispatchEvent(new CustomEvent('form-reset', {
      detail: {},
      bubbles: true
    }));
  }

  private _getFormState(): FormState {
    return {
      data: this._formData,
      errors: this._errors,
      touched: this._touched,
      dirty: this._dirty,
      valid: this._valid,
      submitted: this._submitted
    };
  }

  // Public API methods
  public getData(): FormData {
    return { ...this._formData };
  }

  public setData(data: FormData) {
    this._formData = { ...data };
    this._dirty = true;
    this._validateForm();
    this.requestUpdate();
  }

  public validate(): boolean {
    this._validateForm();
    return this._valid;
  }

  public reset() {
    this._handleReset();
  }

  public getState(): FormState {
    return this._getFormState();
  }

  render() {
    const errorCount = this._errors.filter(e => e.severity === 'error').length;
    const config = this.config || {};
    const showValidationSummary = config.showValidationSummary !== false && 
                                 errorCount > 0 && 
                                 this._submitted;

    const schema = this._currentSchema;
    if (!schema) {
      return html`<div>Loading form...</div>`;
    }

    return html`
      <div class="form-container">
        <!-- Form Header -->
        <div class="form-header">
          <h1 class="form-title">${schema.title}</h1>
          ${schema.description ? html`
            <p class="form-description">${schema.description}</p>
          ` : ''}
        </div>

        <!-- Form Content -->
        <div class="form-sections">
          <!-- Validation Summary -->
          ${showValidationSummary ? html`
            <div class="validation-summary">
              <h4 class="validation-summary-title">Please fix the following errors:</h4>
              <ul class="validation-summary-list">
                ${this._errors
                  .filter(e => e.severity === 'error')
                  .map(error => html`
                    <li class="validation-summary-item">${error.message}</li>
                  `)}
              </ul>
            </div>
          ` : ''}

          <!-- Form Sections or No Selection Message -->
          ${schema.sections.length === 0 ? html`
            <div class="no-roles-message">
              <h3>No Roles Selected</h3>
              <p>Please use the "Filter Roles" button below to select which sections you want to work with.</p>
              <p>Available roles: ${this._allSections.length}</p>
            </div>
          ` : schema.sections.map((section, index) => html`
            <jupiter-form-section
              .section="${section}"
              .columns="${section.columns || this._columns}"
              .formData="${this._formData}"
              .typedMemberData="${this._typedMemberData}"
              .disabled="${this.disabled || this.readonly}"
              .collapsible="${config.collapsibleSections !== false}"
              .locale="${config.locale || 'en-US'}"
              .isFirstSection="${index === 0}"
              .availableDimensions="${this._getAvailableDimensionsForSection(section.id)}"
              @field-change="${this._handleFieldChange}"
              @typed-member-change="${this._handleTypedMemberChange}"
              @section-expand="${this._handleSectionExpand}"
              @concept-expand="${this._handleConceptExpand}"
              @column-remove="${this._handleColumnRemove}"
              @column-add-request="${this._handleColumnAddRequest}"
            ></jupiter-form-section>
          `)}
        </div>

        <!-- Form Actions - Fixed Footer -->
        <div class="form-actions">
          <!-- Filter Roles Button (shown when more than 10 roles) -->
          ${this._shouldShowFilterButton() ? html`
            <button 
              class="filter-roles-button"
              @click="${this._handleFilterRolesClick}"
              ?disabled="${this.disabled || this.readonly}"
            >
              <svg class="filter-icon" viewBox="0 0 24 24">
                <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z"/>
              </svg>
              ${this._selectedRoleIds.length === 0 ? 'Select Roles' : 'Filter Roles'}
              <span class="roles-count">${this._selectedRoleIds.length}/${this._allSections.length}</span>
            </button>
          ` : ''}
          
          <button 
            class="btn-secondary"
            @click="${this._handleSaveDraft}"
            ?disabled="${this.disabled || this.readonly}"
          >
            Save Draft
          </button>
          
          <button 
            class="btn-primary"
            @click="${this._handleSubmit}"
            ?disabled="${this.disabled || this.readonly}"
          >
            Submit
          </button>

          <div class="form-meta">
            <span>Errors: ${errorCount}</span>
            <span>Modified: ${this._dirty ? 'Yes' : 'No'}</span>
            <span>Valid: ${this._valid ? 'Yes' : 'No'}</span>
          </div>
        </div>

        <!-- Filter Roles Dialog -->
        ${this._showFilterDialog ? html`
          <jupiter-filter-roles-dialog
            ?open="${this._showFilterDialog}"
            .availableRoles="${this._allSections}"
            .selectedRoleIds="${this._selectedRoleIds}"
            @dialog-cancel="${this._handleFilterDialogCancel}"
            @roles-filter-apply="${this._handleRoleFilterApply}"
            @click="${this._handleFilterDialogCancel}"
          ></jupiter-filter-roles-dialog>
        ` : ''}
      </div>
    `;
  }
}
